import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  User, BookOpen, Award, Settings, LogOut, TrendingUp, CheckCircle, 
  ChevronRight, Loader2, Mail, Gift, Home, Menu, X, Phone, Shield,
  Upload, Trash2, Lock, Eye, EyeOff, AlertTriangle, BadgeCheck, 
  CreditCard, Percent, FileText, Camera, XCircle, CheckCircle2, Receipt,
  ArrowRightLeft, RefreshCw
} from 'lucide-react';
import { decryptRates } from '../utils/crypto';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Course data
const COURSES = [
  { id: "crypto-basics", title: "Cryptocurrency Fundamentals", lessons: 6, image: "https://images.unsplash.com/photo-1635840420248-1ba20fd1aac2?w=400" },
  { id: "blockchain-101", title: "Blockchain Technology 101", lessons: 8, image: "https://images.pexels.com/photos/8369659/pexels-photo-8369659.jpeg?w=400" },
  { id: "wallet-security", title: "Crypto Wallets & Security", lessons: 7, image: "https://images.unsplash.com/photo-1767972464040-8bfee42d7bed?w=400" },
];

// Acceptable ID types
const ACCEPTABLE_IDS = [
  { id: 'passport', name: 'Passport', icon: FileText },
  { id: 'drivers_license', name: "Driver's License", icon: CreditCard },
  { id: 'national_id', name: 'National ID Card', icon: CreditCard },
  { id: 'pr_card', name: 'PR Card (Canada)', icon: CreditCard },
];

// Sidebar items
const SIDEBAR_ITEMS = [
  { id: 'overview', label: 'Overview', icon: Home },
  { id: 'learning', label: 'My Learning', icon: BookOpen },
  { id: 'invoices', label: 'My Invoices', icon: Receipt, external: '/invoices' },
  { id: 'verification', label: 'ID Verification', icon: Shield },
  { id: 'promotions', label: 'Promotions & Discounts', icon: Gift },
  { id: 'settings', label: 'Account Settings', icon: Settings },
];

export default function Dashboard() {
  const { user, token, logout, loading: authLoading, updateUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const mainContentRef = useRef(null);
  
  // State
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [courseProgress, setCourseProgress] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userSettings, setUserSettings] = useState({
    newsletter_subscribed: true,
    email_verified: false,
    id_verified: false,
    id_status: null, // 'pending', 'approved', 'rejected'
    is_first_responder: false,
  });
  
  // Password change state
  const [passwordForm, setPasswordForm] = useState({ current: '', new: '', confirm: '' });
  const [showPasswords, setShowPasswords] = useState({ current: false, new: false, confirm: false });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  
  // ID upload state
  const [selectedIdType, setSelectedIdType] = useState('');
  const [idFile, setIdFile] = useState(null);
  const [idPreview, setIdPreview] = useState(null);
  const [uploadingId, setUploadingId] = useState(false);
  const [idError, setIdError] = useState('');
  const fileInputRef = useRef(null);
  
  // Delete account state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);
  
  // Currency converter state
  const [converterAmount, setConverterAmount] = useState('1000');
  const [converterFrom, setConverterFrom] = useState('USD');
  const [converterTo, setConverterTo] = useState('CAD');
  const [converterResult, setConverterResult] = useState(null);
  const [rates, setRates] = useState([]);
  const [loadingRates, setLoadingRates] = useState(true);
  
  // Get active tab from URL with state sync
  const [activeTab, setActiveTabState] = useState('overview');
  
  // Sync activeTab with URL changes
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabFromUrl = params.get('tab') || 'overview';
    setActiveTabState(tabFromUrl);
  }, [location.search, location.pathname]);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/auth', { state: { from: { pathname: '/dashboard' } } });
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    const fetchData = async () => {
      if (!token) return;
      setLoading(true);
      
      try {
        const [progressRes, promosRes, settingsRes] = await Promise.all([
          fetch(`${API_URL}/api/user/progress`, { headers: { 'Authorization': `Bearer ${token}` } }),
          fetch(`${API_URL}/api/promotions`),
          fetch(`${API_URL}/api/user/settings`, { headers: { 'Authorization': `Bearer ${token}` } })
        ]);
        
        if (progressRes.ok) setCourseProgress(await progressRes.json());
        if (promosRes.ok) setPromotions(await promosRes.json());
        if (settingsRes.ok) {
          const settings = await settingsRes.json();
          setUserSettings(prev => ({ ...prev, ...settings }));
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
      }
      setLoading(false);
    };

    fetchData();
  }, [token]);

  // Fetch rates for converter
  useEffect(() => {
    const fetchRates = async () => {
      setLoadingRates(true);
      try {
        const res = await fetch(`${API_URL}/api/x7k9`);
        if (res.ok) {
          const data = await res.json();
          // Decrypt the rates
          const decryptedRates = decryptRates(data.d);
          setRates(decryptedRates || []);
        }
      } catch (error) {
        console.error('Failed to fetch rates:', error);
        setRates([]);
      }
      setLoadingRates(false);
    };
    fetchRates();
  }, []);

  // Calculate conversion
  useEffect(() => {
    if (Array.isArray(rates) && rates.length > 0 && converterAmount) {
      const amount = parseFloat(converterAmount);
      if (isNaN(amount)) {
        setConverterResult(null);
        return;
      }
      
      // Find rates for selected currencies
      const fromRate = rates.find(r => r.currency_code === converterFrom);
      const toRate = rates.find(r => r.currency_code === converterTo);
      
      if (converterFrom === 'CAD') {
        // Selling CAD, buying foreign
        if (toRate && toRate.buy_rate > 0) {
          const result = amount / toRate.buy_rate;
          setConverterResult({
            amount: result.toFixed(2),
            rate: (1 / toRate.buy_rate).toFixed(4)
          });
        }
      } else if (converterTo === 'CAD') {
        // Buying CAD with foreign
        if (fromRate && fromRate.sell_rate > 0) {
          const result = amount * fromRate.sell_rate;
          setConverterResult({
            amount: result.toFixed(2),
            rate: fromRate.sell_rate.toFixed(4)
          });
        }
      } else {
        // Cross rate (foreign to foreign via CAD)
        if (fromRate && toRate && fromRate.sell_rate > 0 && toRate.buy_rate > 0) {
          const cadAmount = amount * fromRate.sell_rate;
          const result = cadAmount / toRate.buy_rate;
          setConverterResult({
            amount: result.toFixed(2),
            rate: ((fromRate.sell_rate / toRate.buy_rate)).toFixed(4)
          });
        }
      }
    }
  }, [rates, converterAmount, converterFrom, converterTo]);

  // Scroll to top on tab change
  useEffect(() => {
    if (mainContentRef.current) {
      mainContentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [activeTab]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const setActiveTab = (tabId) => {
    navigate(tabId === 'overview' ? '/dashboard' : `/dashboard?tab=${tabId}`);
    setSidebarOpen(false);
  };

  // Newsletter toggle
  const toggleNewsletter = async () => {
    try {
      const res = await fetch(`${API_URL}/api/user/newsletter`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ subscribed: !userSettings.newsletter_subscribed })
      });
      if (res.ok) {
        setUserSettings(prev => ({ ...prev, newsletter_subscribed: !prev.newsletter_subscribed }));
      }
    } catch (error) {
      console.error('Failed to update newsletter preference:', error);
    }
  };

  // Change password
  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');
    
    if (passwordForm.new !== passwordForm.confirm) {
      setPasswordError('New passwords do not match');
      return;
    }
    if (passwordForm.new.length < 8) {
      setPasswordError('Password must be at least 8 characters');
      return;
    }
    
    setChangingPassword(true);
    try {
      const res = await fetch(`${API_URL}/api/user/change-password`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          current_password: passwordForm.current,
          new_password: passwordForm.new
        })
      });
      
      if (res.ok) {
        setPasswordSuccess('Password changed successfully');
        setPasswordForm({ current: '', new: '', confirm: '' });
      } else {
        const data = await res.json();
        setPasswordError(data.detail || 'Failed to change password');
      }
    } catch (error) {
      setPasswordError('Failed to change password');
    }
    setChangingPassword(false);
  };

  // ID file handling
  const handleIdFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      setIdError('Please upload a JPG, PNG, or PDF file');
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setIdError('File size must be less than 5MB');
      return;
    }
    
    setIdError('');
    setIdFile(file);
    
    // Create preview for images
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => setIdPreview(e.target.result);
      reader.readAsDataURL(file);
    } else {
      setIdPreview(null);
    }
  };

  // Upload ID
  const handleUploadId = async () => {
    if (!selectedIdType || !idFile) {
      setIdError('Please select an ID type and upload a file');
      return;
    }
    
    setUploadingId(true);
    setIdError('');
    
    try {
      const formData = new FormData();
      formData.append('id_type', selectedIdType);
      formData.append('file', idFile);
      
      const res = await fetch(`${API_URL}/api/user/upload-id`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (res.ok) {
        setUserSettings(prev => ({ ...prev, id_status: 'pending' }));
        setIdFile(null);
        setIdPreview(null);
        setSelectedIdType('');
      } else {
        const data = await res.json();
        setIdError(data.detail || 'Failed to upload ID');
      }
    } catch (error) {
      setIdError('Failed to upload ID. Please try again.');
    }
    setUploadingId(false);
  };

  // Delete account
  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') {
      return;
    }
    
    setDeletingAccount(true);
    try {
      const res = await fetch(`${API_URL}/api/user/delete-account`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        logout();
        navigate('/');
      }
    } catch (error) {
      console.error('Failed to delete account:', error);
    }
    setDeletingAccount(false);
  };

  // Calculate stats
  const coursesStarted = courseProgress.length;
  const coursesCompleted = courseProgress.filter(p => p.is_completed).length;
  const totalLessonsCompleted = courseProgress.reduce((acc, p) => acc + (p.completed_lessons?.length || 0), 0);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#001435] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#3B82F6] animate-spin" />
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#001435]" data-testid="dashboard-page">
      {/* Mobile Header - Fixed */}
      <div className="lg:hidden bg-[#001020] border-b border-white/10 px-4 py-3 flex items-center justify-between fixed top-0 left-0 right-0 z-50">
        <button
          onClick={() => setSidebarOpen(true)}
          className="p-2 text-white hover:bg-white/10 rounded-lg transition-colors"
          data-testid="mobile-menu-btn"
        >
          <Menu className="w-6 h-6" />
        </button>
        <h1 className="text-white font-semibold">Dashboard</h1>
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#3B82F6] to-purple-500 flex items-center justify-center text-white text-sm font-bold">
          {user.first_name?.charAt(0)}{user.last_name?.charAt(0)}
        </div>
      </div>

      <div className="flex lg:pt-0 pt-14">
        {/* Sidebar Overlay */}
        {sidebarOpen && (
          <div 
            className="fixed inset-0 bg-black/60 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Sidebar */}
        <aside className={`fixed lg:sticky lg:top-0 inset-y-0 left-0 z-50 w-72 bg-[#001020] border-r border-white/10 transform transition-transform duration-300 lg:transform-none h-screen overflow-y-auto ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden absolute top-4 right-4 p-2 text-white/60 hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>

          {/* User Info */}
          <div className="p-6 border-b border-white/10">
            <div className="flex items-center gap-4">
              <div className="relative">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#3B82F6] to-purple-500 flex items-center justify-center text-white text-xl font-bold flex-shrink-0">
                  {user.first_name?.charAt(0)}{user.last_name?.charAt(0)}
                </div>
                {userSettings.id_verified && (
                  <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-emerald-500 rounded-full flex items-center justify-center">
                    <BadgeCheck className="w-3 h-3 text-white" />
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-semibold text-lg truncate">{user.first_name} {user.last_name}</p>
                <p className="text-blue-100/50 text-sm truncate">{user.email}</p>
                {userSettings.id_verified && (
                  <span className="inline-flex items-center gap-1 text-xs text-emerald-400 mt-1">
                    <BadgeCheck className="w-3 h-3" /> Verified Member
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="p-4 space-y-1">
            {SIDEBAR_ITEMS.map(item => {
              const Icon = item.icon;
              const isActive = activeTab === item.id;
              
              // Handle external links (like /invoices)
              if (item.external) {
                return (
                  <Link
                    key={item.id}
                    to={item.external}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all text-blue-100/60 hover:bg-white/5 hover:text-white`}
                    data-testid={`sidebar-${item.id}`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="font-medium">{item.label}</span>
                    {user?.customer_id && (
                      <span className="ml-auto px-2 py-0.5 text-xs bg-emerald-500/20 text-emerald-400 rounded-full">Linked</span>
                    )}
                  </Link>
                );
              }
              
              return (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(item.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                    isActive 
                      ? 'bg-[#3B82F6] text-white shadow-lg shadow-[#3B82F6]/20' 
                      : 'text-blue-100/60 hover:bg-white/5 hover:text-white'
                  }`}
                  data-testid={`sidebar-${item.id}`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{item.label}</span>
                  {item.id === 'verification' && userSettings.id_status === 'pending' && (
                    <span className="ml-auto w-2 h-2 bg-yellow-400 rounded-full" />
                  )}
                </button>
              );
            })}
          </nav>

          {/* Quick Links */}
          <div className="p-4 border-t border-white/10">
            <p className="text-xs text-blue-100/40 uppercase tracking-wider mb-3 px-4">Quick Links</p>
            <Link to="/crypto/academy" className="flex items-center gap-3 px-4 py-2.5 text-blue-100/60 hover:text-white hover:bg-white/5 rounded-lg transition-colors">
              <BookOpen className="w-4 h-4" />
              <span className="text-sm">FonderX Academy</span>
            </Link>
            <Link to="/crypto" className="flex items-center gap-3 px-4 py-2.5 text-blue-100/60 hover:text-white hover:bg-white/5 rounded-lg transition-colors">
              <TrendingUp className="w-4 h-4" />
              <span className="text-sm">Live Prices</span>
            </Link>
            <Link to="/contact" className="flex items-center gap-3 px-4 py-2.5 text-blue-100/60 hover:text-white hover:bg-white/5 rounded-lg transition-colors">
              <Phone className="w-4 h-4" />
              <span className="text-sm">Contact Us</span>
            </Link>
          </div>

          {/* Logout */}
          <div className="p-4 border-t border-white/10">
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-3 text-red-400 hover:bg-red-500/10 rounded-xl transition-colors"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
              <span className="font-medium">Log Out</span>
            </button>
          </div>
        </aside>

        {/* Main Content */}
        <main ref={mainContentRef} className="flex-1 min-w-0 p-4 sm:p-6 lg:p-8 overflow-y-auto">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6 max-w-6xl">
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">Welcome back, {user.first_name}!</h1>
                <p className="text-blue-100/60">Here's your account overview</p>
              </div>

              {/* Verified Badge Banner */}
              {userSettings.id_verified && (
                <div className="bg-gradient-to-r from-emerald-500/20 to-emerald-500/5 rounded-xl border border-emerald-500/30 p-4 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                    <BadgeCheck className="w-6 h-6 text-emerald-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">Verified Member</h3>
                    <p className="text-sm text-emerald-300/80">You qualify for preferred exchange rates!</p>
                  </div>
                </div>
              )}

              {/* Stats Grid */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                <div className="bg-gradient-to-br from-[#001a40] to-[#002050] rounded-xl border border-white/10 p-4 sm:p-5">
                  <BookOpen className="w-7 h-7 sm:w-8 sm:h-8 text-[#3B82F6] mb-2 sm:mb-3" />
                  <div className="text-xl sm:text-2xl font-bold text-white">{coursesStarted}</div>
                  <div className="text-xs sm:text-sm text-blue-100/60">Courses Started</div>
                </div>
                <div className="bg-gradient-to-br from-[#001a40] to-[#002050] rounded-xl border border-white/10 p-4 sm:p-5">
                  <Award className="w-7 h-7 sm:w-8 sm:h-8 text-emerald-400 mb-2 sm:mb-3" />
                  <div className="text-xl sm:text-2xl font-bold text-white">{coursesCompleted}</div>
                  <div className="text-xs sm:text-sm text-blue-100/60">Completed</div>
                </div>
                <div className="bg-gradient-to-br from-[#001a40] to-[#002050] rounded-xl border border-white/10 p-4 sm:p-5">
                  <CheckCircle className="w-7 h-7 sm:w-8 sm:h-8 text-purple-400 mb-2 sm:mb-3" />
                  <div className="text-xl sm:text-2xl font-bold text-white">{totalLessonsCompleted}</div>
                  <div className="text-xs sm:text-sm text-blue-100/60">Lessons Done</div>
                </div>
                <div className="bg-gradient-to-br from-[#001a40] to-[#002050] rounded-xl border border-white/10 p-4 sm:p-5">
                  <Gift className="w-7 h-7 sm:w-8 sm:h-8 text-amber-400 mb-2 sm:mb-3" />
                  <div className="text-xl sm:text-2xl font-bold text-white">{promotions.length}</div>
                  <div className="text-xs sm:text-sm text-blue-100/60">Active Promos</div>
                </div>
              </div>

              {/* Action Cards */}
              <div className="grid sm:grid-cols-2 gap-4">
                <Link
                  to="/crypto/academy"
                  className="bg-gradient-to-br from-[#3B82F6]/20 to-[#3B82F6]/5 rounded-xl border border-[#3B82F6]/30 hover:border-[#3B82F6]/50 p-5 sm:p-6 transition-all group"
                >
                  <BookOpen className="w-9 h-9 sm:w-10 sm:h-10 text-[#3B82F6] mb-3 sm:mb-4" />
                  <h3 className="text-base sm:text-lg font-semibold text-white mb-2 group-hover:text-[#3B82F6] transition-colors">Continue Learning</h3>
                  <p className="text-sm text-blue-100/60">Expand your crypto knowledge with FonderX Academy</p>
                </Link>

                {!userSettings.id_verified && (
                  <button
                    onClick={() => setActiveTab('verification')}
                    className="bg-gradient-to-br from-emerald-500/20 to-emerald-500/5 rounded-xl border border-emerald-500/30 hover:border-emerald-500/50 p-5 sm:p-6 transition-all group text-left"
                  >
                    <Shield className="w-9 h-9 sm:w-10 sm:h-10 text-emerald-400 mb-3 sm:mb-4" />
                    <h3 className="text-base sm:text-lg font-semibold text-white mb-2 group-hover:text-emerald-400 transition-colors">Verify Your Identity</h3>
                    <p className="text-sm text-blue-100/60">Get preferred rates and unlock exclusive benefits</p>
                  </button>
                )}

                {userSettings.id_verified && (
                  <Link
                    to="/contact"
                    className="bg-gradient-to-br from-purple-500/20 to-purple-500/5 rounded-xl border border-purple-500/30 hover:border-purple-500/50 p-5 sm:p-6 transition-all group"
                  >
                    <Phone className="w-9 h-9 sm:w-10 sm:h-10 text-purple-400 mb-3 sm:mb-4" />
                    <h3 className="text-base sm:text-lg font-semibold text-white mb-2 group-hover:text-purple-400 transition-colors">Contact FonderX</h3>
                    <p className="text-sm text-blue-100/60">Get in touch for assistance or exchange inquiries</p>
                  </Link>
                )}
              </div>

              {/* Currency Converter & Rate Comparison */}
              <div className="grid lg:grid-cols-2 gap-6">
                {/* Currency Converter */}
                <div className="bg-gradient-to-br from-[#001a40] to-[#002050] rounded-xl border border-white/10 p-5 sm:p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <ArrowRightLeft className="w-6 h-6 text-[#3B82F6]" />
                    <h3 className="text-lg font-semibold text-white">Currency Converter</h3>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-blue-100/60 text-sm mb-1.5">Amount</label>
                      <input
                        type="number"
                        value={converterAmount}
                        onChange={(e) => setConverterAmount(e.target.value)}
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white text-lg placeholder-blue-100/30 focus:outline-none focus:border-[#3B82F6]"
                        placeholder="Enter amount"
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-blue-100/60 text-sm mb-1.5">From</label>
                        <select
                          value={converterFrom}
                          onChange={(e) => setConverterFrom(e.target.value)}
                          className="w-full px-4 py-3 bg-[#0a1628] border border-white/10 rounded-xl text-white focus:outline-none focus:border-[#3B82F6] appearance-none cursor-pointer"
                          style={{ backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`, backgroundPosition: 'right 0.5rem center', backgroundRepeat: 'no-repeat', backgroundSize: '1.5em 1.5em', paddingRight: '2.5rem' }}
                        >
                          <option value="CAD" className="bg-[#0a1628] text-white">CAD</option>
                          {Array.isArray(rates) && rates.map(r => (
                            <option key={r.currency_code} value={r.currency_code} className="bg-[#0a1628] text-white">{r.currency_code}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-blue-100/60 text-sm mb-1.5">To</label>
                        <select
                          value={converterTo}
                          onChange={(e) => setConverterTo(e.target.value)}
                          className="w-full px-4 py-3 bg-[#0a1628] border border-white/10 rounded-xl text-white focus:outline-none focus:border-[#3B82F6] appearance-none cursor-pointer"
                          style={{ backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`, backgroundPosition: 'right 0.5rem center', backgroundRepeat: 'no-repeat', backgroundSize: '1.5em 1.5em', paddingRight: '2.5rem' }}
                        >
                          <option value="CAD" className="bg-[#0a1628] text-white">CAD</option>
                          {Array.isArray(rates) && rates.map(r => (
                            <option key={r.currency_code} value={r.currency_code} className="bg-[#0a1628] text-white">{r.currency_code}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    
                    {converterResult && (
                      <div className="bg-[#3B82F6]/10 border border-[#3B82F6]/30 rounded-xl p-4">
                        <div className="text-lg sm:text-xl lg:text-2xl font-bold text-white break-words">
                          {parseFloat(converterAmount).toLocaleString()} {converterFrom} = {parseFloat(converterResult.amount).toLocaleString()} {converterTo}
                        </div>
                        <div className="text-xs sm:text-sm text-blue-100/60 mt-1">
                          Rate: 1 {converterFrom} = {converterResult.rate} {converterTo}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Rate Comparison */}
                <div className="bg-gradient-to-br from-[#001a40] to-[#002050] rounded-xl border border-white/10 p-5 sm:p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <TrendingUp className="w-6 h-6 text-emerald-400" />
                      <h3 className="text-lg font-semibold text-white">Your Rates</h3>
                    </div>
                    {userSettings.id_verified && (
                      <span className="px-2 py-1 bg-emerald-500/20 text-emerald-400 text-xs rounded-full">Preferred</span>
                    )}
                  </div>
                  
                  <div className="space-y-3">
                    {/* Desktop header */}
                    <div className="hidden sm:grid grid-cols-4 gap-2 text-xs text-blue-100/60 pb-2 border-b border-white/10">
                      <span>Currency</span>
                      <span className="text-center">Regular</span>
                      <span className="text-center">Your Rate</span>
                      <span className="text-right">Savings</span>
                    </div>
                    
                    {loadingRates ? (
                      <div className="text-center py-4">
                        <RefreshCw className="w-5 h-5 animate-spin text-blue-100/60 mx-auto" />
                      </div>
                    ) : Array.isArray(rates) && rates.length > 0 ? (
                      rates.slice(0, 5).map(rate => {
                        const sellRate = rate.sell_rate || 0;
                        const preferredRate = userSettings.id_verified 
                          ? (sellRate * 1.002).toFixed(4) 
                          : sellRate.toFixed(4);
                        const savings = userSettings.id_verified
                          ? ((parseFloat(preferredRate) - sellRate) * 1000).toFixed(2)
                          : '0.00';
                        
                        return (
                          <div key={rate.currency_code} className="py-2 border-b border-white/5">
                            {/* Desktop view */}
                            <div className="hidden sm:grid grid-cols-4 gap-2 items-center">
                              <div className="flex items-center gap-2">
                                <span className="text-lg">{rate.flag}</span>
                                <span className="text-white font-medium">{rate.currency_code}</span>
                              </div>
                              <span className="text-center text-blue-100/60 text-sm">{sellRate.toFixed(4)}</span>
                              <span className={`text-center font-medium text-sm ${userSettings.id_verified ? 'text-emerald-400' : 'text-white'}`}>
                                {preferredRate}
                              </span>
                              <span className={`text-right text-sm ${userSettings.id_verified ? 'text-emerald-400' : 'text-blue-100/40'}`}>
                                {userSettings.id_verified ? `+$${savings}` : '-'}
                              </span>
                            </div>
                            {/* Mobile view */}
                            <div className="sm:hidden flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <span className="text-lg">{rate.flag}</span>
                                <span className="text-white font-medium">{rate.currency_code}</span>
                              </div>
                              <div className="text-right">
                                <div className={`font-medium ${userSettings.id_verified ? 'text-emerald-400' : 'text-white'}`}>
                                  {preferredRate}
                                </div>
                                {userSettings.id_verified && (
                                  <div className="text-xs text-emerald-400">+${savings}</div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="text-center py-4 text-blue-100/60 text-sm">
                        No rates available
                      </div>
                    )}
                    
                    {!userSettings.id_verified && (
                      <div className="mt-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                        <p className="text-amber-400 text-xs sm:text-sm">
                          <span className="font-medium">Verify your ID</span> to unlock preferred rates!
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Learning Tab */}
          {activeTab === 'learning' && (
            <div className="space-y-6 max-w-4xl">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <h1 className="text-2xl font-bold text-white mb-2">My Learning</h1>
                  <p className="text-blue-100/60">Track your course progress</p>
                </div>
                <Link
                  to="/crypto/academy"
                  className="flex items-center gap-2 text-[#3B82F6] hover:text-[#60A5FA] transition-colors text-sm sm:text-base"
                >
                  Browse Courses <ChevronRight className="w-4 h-4" />
                </Link>
              </div>

              <div className="space-y-3">
                {COURSES.map(course => {
                  const progress = courseProgress.find(p => p.course_id === course.id);
                  const progressPercent = progress
                    ? Math.round((progress.completed_lessons?.length || 0) / course.lessons * 100)
                    : 0;
                  
                  return (
                    <Link
                      key={course.id}
                      to="/crypto/academy"
                      className="flex items-center gap-3 sm:gap-4 bg-gradient-to-br from-[#001a40] to-[#002050] rounded-xl border border-white/10 hover:border-[#3B82F6]/50 p-3 sm:p-4 transition-all group"
                    >
                      <img src={course.image} alt={course.title} className="w-14 h-14 sm:w-16 sm:h-16 rounded-lg object-cover flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-white text-sm sm:text-base group-hover:text-[#3B82F6] transition-colors truncate">{course.title}</h3>
                        <div className="flex items-center gap-3 mt-2">
                          <div className="flex-1 max-w-32 h-2 bg-white/10 rounded-full overflow-hidden">
                            <div className="h-full bg-[#3B82F6] transition-all" style={{ width: `${progressPercent}%` }} />
                          </div>
                          <span className="text-xs text-blue-100/50">{progressPercent}%</span>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-blue-100/40 group-hover:text-[#3B82F6] flex-shrink-0" />
                    </Link>
                  );
                })}
              </div>
            </div>
          )}

          {/* Verification Tab */}
          {activeTab === 'verification' && (
            <div className="space-y-6 max-w-2xl">
              <div>
                <h1 className="text-2xl font-bold text-white mb-2">ID Verification</h1>
                <p className="text-blue-100/60">Verify your identity to unlock preferred rates</p>
              </div>

              {/* Benefits */}
              <div className="bg-gradient-to-br from-[#001a40] to-[#002050] rounded-xl border border-white/10 p-5 sm:p-6">
                <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                  <BadgeCheck className="w-5 h-5 text-emerald-400" />
                  Verification Benefits
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-center gap-3 text-blue-100/70">
                    <Percent className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                    <span>Preferred exchange rates (0.1% better)</span>
                  </li>
                  <li className="flex items-center gap-3 text-blue-100/70">
                    <TrendingUp className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                    <span>Higher transaction limits</span>
                  </li>
                  <li className="flex items-center gap-3 text-blue-100/70">
                    <Gift className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                    <span>Access to exclusive promotions</span>
                  </li>
                </ul>
              </div>

              {/* Status */}
              {userSettings.id_verified ? (
                <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-6 text-center">
                  <CheckCircle2 className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">Identity Verified</h3>
                  <p className="text-emerald-300/80">You're enjoying preferred rates and benefits!</p>
                </div>
              ) : userSettings.id_status === 'pending' ? (
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-6 text-center">
                  <Loader2 className="w-16 h-16 text-yellow-400 mx-auto mb-4 animate-spin" />
                  <h3 className="text-xl font-semibold text-white mb-2">Verification Pending</h3>
                  <p className="text-yellow-300/80">Your ID is being reviewed. This usually takes 1-2 business days.</p>
                </div>
              ) : userSettings.id_status === 'rejected' ? (
                <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6">
                  <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2 text-center">Verification Rejected</h3>
                  <p className="text-red-300/80 text-center mb-4">Please submit a clearer image of your ID.</p>
                </div>
              ) : (
                <>
                  {/* Acceptable IDs */}
                  <div className="bg-gradient-to-br from-[#001a40] to-[#002050] rounded-xl border border-white/10 p-5 sm:p-6">
                    <h3 className="font-semibold text-white mb-4">Acceptable Government-Issued IDs</h3>
                    <div className="grid grid-cols-2 gap-3">
                      {ACCEPTABLE_IDS.map(idType => (
                        <button
                          key={idType.id}
                          onClick={() => setSelectedIdType(idType.id)}
                          className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${
                            selectedIdType === idType.id
                              ? 'border-[#3B82F6] bg-[#3B82F6]/10 text-white'
                              : 'border-white/10 text-blue-100/60 hover:border-white/20 hover:text-white'
                          }`}
                        >
                          <idType.icon className="w-5 h-5 flex-shrink-0" />
                          <span className="text-sm">{idType.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Upload Section */}
                  <div className="bg-gradient-to-br from-[#001a40] to-[#002050] rounded-xl border border-white/10 p-5 sm:p-6">
                    <h3 className="font-semibold text-white mb-4">Upload Your ID</h3>
                    
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleIdFileChange}
                      accept="image/jpeg,image/png,image/jpg,application/pdf"
                      className="hidden"
                    />
                    
                    {idPreview ? (
                      <div className="relative mb-4">
                        <img src={idPreview} alt="ID Preview" className="w-full max-h-64 object-contain rounded-lg" />
                        <button
                          onClick={() => { setIdFile(null); setIdPreview(null); }}
                          className="absolute top-2 right-2 p-1 bg-red-500 rounded-full text-white"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : idFile ? (
                      <div className="flex items-center gap-3 mb-4 p-3 bg-white/5 rounded-lg">
                        <FileText className="w-8 h-8 text-[#3B82F6]" />
                        <span className="text-white text-sm">{idFile.name}</span>
                        <button
                          onClick={() => setIdFile(null)}
                          className="ml-auto p-1 text-red-400 hover:text-red-300"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        className="w-full border-2 border-dashed border-white/20 rounded-xl p-8 text-center hover:border-[#3B82F6]/50 transition-colors mb-4"
                      >
                        <Camera className="w-12 h-12 text-blue-100/40 mx-auto mb-3" />
                        <p className="text-white font-medium mb-1">Click to upload</p>
                        <p className="text-blue-100/50 text-sm">JPG, PNG or PDF (max 5MB)</p>
                      </button>
                    )}

                    {idError && (
                      <p className="text-red-400 text-sm mb-4">{idError}</p>
                    )}

                    <button
                      onClick={handleUploadId}
                      disabled={!selectedIdType || !idFile || uploadingId}
                      className="w-full bg-[#3B82F6] hover:bg-[#2563EB] disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium py-3 rounded-lg transition-colors flex items-center justify-center gap-2"
                    >
                      {uploadingId ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload className="w-5 h-5" />
                          Submit for Verification
                        </>
                      )}
                    </button>
                  </div>

                  {/* Security Note */}
                  <div className="bg-white/5 rounded-xl p-4 flex items-start gap-3">
                    <Shield className="w-5 h-5 text-[#3B82F6] flex-shrink-0 mt-0.5" />
                    <p className="text-blue-100/60 text-sm">
                      Your ID is encrypted and stored securely. We comply with all privacy regulations and will never share your information with third parties.
                    </p>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Promotions Tab */}
          {activeTab === 'promotions' && (
            <div className="space-y-6 max-w-4xl">
              <div>
                <h1 className="text-2xl font-bold text-white mb-2">Promotions & Discounts</h1>
                <p className="text-blue-100/60">Exclusive offers for FonderX members</p>
              </div>

              {/* First Responder Discount */}
              <div className="bg-gradient-to-br from-red-500/10 to-orange-500/10 rounded-xl border border-red-500/30 p-5 sm:p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-red-500/20 flex items-center justify-center flex-shrink-0">
                    <Shield className="w-6 h-6 text-red-400" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-white mb-2">First Responder Discount</h3>
                    <p className="text-blue-100/60 text-sm mb-3">
                      Police, firefighters, paramedics, and healthcare workers get 0.15% better rates on all exchanges.
                    </p>
                    <Link
                      to="/contact"
                      className="text-red-400 text-sm font-medium hover:text-red-300 transition-colors"
                    >
                      Contact us to verify your status →
                    </Link>
                  </div>
                </div>
              </div>

              {/* Verified Member Rates */}
              {userSettings.id_verified && (
                <div className="bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 rounded-xl border border-emerald-500/30 p-5 sm:p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                      <BadgeCheck className="w-6 h-6 text-emerald-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white mb-2">Preferred Member Rates</h3>
                      <p className="text-emerald-300/80 text-sm">
                        You're enjoying 0.1% better rates as a verified member!
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Other Promotions */}
              <div className="space-y-4">
                <h3 className="font-semibold text-white">Current Promotions</h3>
                {promotions.length === 0 ? (
                  <div className="text-center py-12 bg-gradient-to-br from-[#001a40] to-[#002050] rounded-xl border border-white/10">
                    <Gift className="w-14 h-14 text-blue-100/20 mx-auto mb-4" />
                    <p className="text-blue-100/60">No active promotions at the moment</p>
                  </div>
                ) : (
                  promotions.map((promo, idx) => (
                    <div key={idx} className="bg-gradient-to-br from-[#001a40] to-[#002050] rounded-xl border border-white/10 p-5">
                      <div className="flex items-start gap-4">
                        <div className="w-11 h-11 rounded-xl bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                          <Gift className="w-5 h-5 text-amber-400" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-white mb-1">{promo.title}</h3>
                          <p className="text-blue-100/60 text-sm">{promo.content}</p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <div className="space-y-6 max-w-2xl">
              <div>
                <h1 className="text-2xl font-bold text-white mb-2">Account Settings</h1>
                <p className="text-blue-100/60">Manage your profile and preferences</p>
              </div>
              
              {/* Profile Info */}
              <div className="bg-gradient-to-br from-[#001a40] to-[#002050] rounded-xl border border-white/10 p-5 sm:p-6">
                <h3 className="font-semibold text-white mb-4">Profile Information</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-blue-100/60 mb-1">First Name</label>
                    <div className="bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white">{user.first_name}</div>
                  </div>
                  <div>
                    <label className="block text-sm text-blue-100/60 mb-1">Last Name</label>
                    <div className="bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white">{user.last_name}</div>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-sm text-blue-100/60 mb-1">Email</label>
                    <div className="bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white">{user.email}</div>
                  </div>
                </div>
              </div>

              {/* Email Preferences */}
              <div className="bg-gradient-to-br from-[#001a40] to-[#002050] rounded-xl border border-white/10 p-5 sm:p-6">
                <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                  <Mail className="w-5 h-5" />
                  Email Preferences
                </h3>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white">Newsletter & Promotions</p>
                    <p className="text-blue-100/50 text-sm">Receive updates on rates and offers</p>
                  </div>
                  <button
                    onClick={toggleNewsletter}
                    className={`relative w-12 h-7 rounded-full transition-colors ${
                      userSettings.newsletter_subscribed ? 'bg-[#3B82F6]' : 'bg-white/20'
                    }`}
                  >
                    <div className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-transform ${
                      userSettings.newsletter_subscribed ? 'left-6' : 'left-1'
                    }`} />
                  </button>
                </div>
              </div>

              {/* Change Password */}
              <div className="bg-gradient-to-br from-[#001a40] to-[#002050] rounded-xl border border-white/10 p-5 sm:p-6">
                <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                  <Lock className="w-5 h-5" />
                  Change Password
                </h3>
                <form onSubmit={handleChangePassword} className="space-y-4">
                  <div>
                    <label className="block text-sm text-blue-100/60 mb-1">Current Password</label>
                    <div className="relative">
                      <input
                        type={showPasswords.current ? 'text' : 'password'}
                        value={passwordForm.current}
                        onChange={(e) => setPasswordForm(prev => ({ ...prev, current: e.target.value }))}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white pr-10 focus:border-[#3B82F6] focus:outline-none"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPasswords(prev => ({ ...prev, current: !prev.current }))}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-blue-100/50"
                      >
                        {showPasswords.current ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-blue-100/60 mb-1">New Password</label>
                    <div className="relative">
                      <input
                        type={showPasswords.new ? 'text' : 'password'}
                        value={passwordForm.new}
                        onChange={(e) => setPasswordForm(prev => ({ ...prev, new: e.target.value }))}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white pr-10 focus:border-[#3B82F6] focus:outline-none"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-blue-100/50"
                      >
                        {showPasswords.new ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-blue-100/60 mb-1">Confirm New Password</label>
                    <div className="relative">
                      <input
                        type={showPasswords.confirm ? 'text' : 'password'}
                        value={passwordForm.confirm}
                        onChange={(e) => setPasswordForm(prev => ({ ...prev, confirm: e.target.value }))}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white pr-10 focus:border-[#3B82F6] focus:outline-none"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }))}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-blue-100/50"
                      >
                        {showPasswords.confirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                  
                  {passwordError && (
                    <p className="text-red-400 text-sm">{passwordError}</p>
                  )}
                  {passwordSuccess && (
                    <p className="text-emerald-400 text-sm">{passwordSuccess}</p>
                  )}
                  
                  <button
                    type="submit"
                    disabled={changingPassword}
                    className="bg-[#3B82F6] hover:bg-[#2563EB] disabled:opacity-50 text-white font-medium py-3 px-6 rounded-lg transition-colors flex items-center gap-2"
                  >
                    {changingPassword ? <Loader2 className="w-5 h-5 animate-spin" /> : <Lock className="w-5 h-5" />}
                    Change Password
                  </button>
                </form>
              </div>

              {/* Delete Account */}
              <div className="bg-gradient-to-br from-red-500/10 to-red-500/5 rounded-xl border border-red-500/30 p-5 sm:p-6">
                <h3 className="font-semibold text-white mb-2 flex items-center gap-2">
                  <Trash2 className="w-5 h-5 text-red-400" />
                  Delete Account
                </h3>
                <p className="text-blue-100/60 text-sm mb-4">
                  Permanently delete your account and all associated data. This action cannot be undone.
                </p>
                
                {!showDeleteConfirm ? (
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="bg-red-500/20 hover:bg-red-500/30 text-red-400 font-medium py-2 px-4 rounded-lg transition-colors"
                  >
                    Delete My Account
                  </button>
                ) : (
                  <div className="space-y-4">
                    <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-red-400 font-medium">Are you absolutely sure?</p>
                          <p className="text-red-300/70 text-sm mt-1">
                            This will permanently delete your account, course progress, and all data. Type DELETE to confirm.
                          </p>
                        </div>
                      </div>
                    </div>
                    <input
                      type="text"
                      placeholder="Type DELETE to confirm"
                      value={deleteConfirmText}
                      onChange={(e) => setDeleteConfirmText(e.target.value)}
                      className="w-full bg-white/5 border border-red-500/30 rounded-lg px-4 py-3 text-white focus:border-red-500 focus:outline-none"
                    />
                    <div className="flex gap-3">
                      <button
                        onClick={() => { setShowDeleteConfirm(false); setDeleteConfirmText(''); }}
                        className="bg-white/10 hover:bg-white/20 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleDeleteAccount}
                        disabled={deleteConfirmText !== 'DELETE' || deletingAccount}
                        className="bg-red-500 hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium py-2 px-4 rounded-lg transition-colors flex items-center gap-2"
                      >
                        {deletingAccount ? <Loader2 className="w-5 h-5 animate-spin" /> : <Trash2 className="w-5 h-5" />}
                        Delete Forever
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
